// useReportIt (v7 §6.1, MV-1077) — the stateful bridge between a Report It
// dialog and the submission flow. Owns the idempotency nonce (stable for the
// dialog's lifetime), the submit lifecycle (idle → submitting → success/error),
// and the default video-element lookup.
//
// Decoupled from MytaverseEventProvider: the report context (environment,
// event, session id, correlation entities, optional getStats) is passed in, so
// the hook is unit-testable and the component that mounts it owns the (thin)
// mapping from live context to this config.

import { useCallback, useMemo, useRef, useState } from 'react';

import {
  runReportItSubmission,
  retryAttachmentDelivery,
  AttachmentDeliveryOutcome,
  ReportDraft,
  ReportSubmissionPhase,
} from './submitReportFlow';
import { captureReportItSnapshot } from './captureSnapshot';
import { getStreamConnectionSnapshot } from './connectionStateStore';
import { getBufferedRtcSamples } from './rtcStatsBuffer';
import { getScrubbedBrowserLogs } from './browserLogBuffer';
import {
  submitReport,
  ReportCorrelationEntities,
  ReportAttachmentFile,
  SubmitReportResponse,
  ReportIncidentType,
  ReportSymptomHint,
  ReportSeverityHint,
} from './reportItApi';
import { generateClientReportNonce } from './reportNonce';

const GAMECAST_VIDEO_ELEMENT_ID = 'gamecastVideoTag';

export interface ReportItContext {
  environment: string;
  eventId?: string;
  browserSessionId: string;
  entities?: ReportCorrelationEntities;
  /** Web Hub build version (MV-1396). */
  hubVersion?: string;
  /** Spatial Manager service version the client saw at startup (MV-1396). */
  spatialManagerVersion?: string;
  /** Optional WebRTC stats provider — omit until the GameCast peer connection
   *  is exposed; the capture degrades to media-element + black-frame signals. */
  getStats?: () => Promise<RTCStatsReport>;
  /** Override the video-element lookup (defaults to #gamecastVideoTag). */
  getVideoElement?: () => HTMLVideoElement | null;
}

export interface ReportFormValues {
  incidentType: ReportIncidentType;
  description?: string;
  symptomHints?: ReportSymptomHint[];
  severityHint?: ReportSeverityHint;
  attachDiagnostics?: boolean;
  attachments?: ReportAttachmentFile[];
}

export type ReportItStatus = 'idle' | 'submitting' | 'success' | 'error';

export type ReportItPhase = 'idle' | ReportSubmissionPhase;

export interface UseReportItResult {
  status: ReportItStatus;
  /** Finer-grained progress within `submitting` (capturing/submitting/uploading). */
  phase: ReportItPhase;
  /** Per-attachment upload progress (0..1), keyed by declaration index. */
  uploadProgress: Record<number, number>;
  /** True when the report was created but an attachment failed to upload. */
  attachmentUploadFailed: boolean;
  /** Declaration indexes whose bytes never landed — offer these a retry. */
  failedAttachmentIndexes: number[];
  /** Declaration indexes with a retry in flight. */
  retryingAttachmentIndexes: number[];
  error?: string;
  result?: SubmitReportResponse;
  submit: (values: ReportFormValues) => Promise<SubmitReportResponse | undefined>;
  /** Take a fresh upload attempt for one failed attachment (§8.2). */
  retryAttachment: (index: number) => Promise<boolean>;
  reset: () => void;
}

export function useReportIt(context: ReportItContext): UseReportItResult {
  const {
    environment,
    eventId,
    browserSessionId,
    entities,
    hubVersion,
    spatialManagerVersion,
    getStats,
    getVideoElement: getVideoElementOverride,
  } = context;

  const [status, setStatus] = useState<ReportItStatus>('idle');
  const [phase, setPhase] = useState<ReportItPhase>('idle');
  const [uploadProgress, setUploadProgress] = useState<Record<number, number>>(
    {},
  );
  const [attachmentUploadFailed, setAttachmentUploadFailed] = useState(false);
  const [failedAttachments, setFailedAttachments] = useState<
    Record<number, string | undefined>
  >({});
  const [retryingAttachments, setRetryingAttachments] = useState<
    Record<number, boolean>
  >({});
  const [error, setError] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<SubmitReportResponse | undefined>(undefined);

  // The files behind the last submitted manifest, so a retry can re-send the
  // exact bytes the backend already signed a part plan for.
  const submittedFilesRef = useRef<ReportAttachmentFile[]>([]);

  // Stable across retries of a single report attempt (so a resubmit collapses
  // onto the same report, v7 §10.3), but regenerated by reset() when a new
  // report begins. Without that, reopening the dialog reuses the nonce and the
  // next report idempotently collapses onto the previous one.
  const [nonce, setNonce] = useState<string>(() => generateClientReportNonce());

  const getVideoElement = useMemo(
    () =>
      getVideoElementOverride ??
      ((): HTMLVideoElement | null =>
        document.getElementById(
          GAMECAST_VIDEO_ELEMENT_ID,
        ) as HTMLVideoElement | null),
    [getVideoElementOverride],
  );

  const submit = useCallback(
    async (
      values: ReportFormValues,
    ): Promise<SubmitReportResponse | undefined> => {
      setStatus('submitting');
      setError(undefined);
      setUploadProgress({});
      setAttachmentUploadFailed(false);
      setFailedAttachments({});
      setRetryingAttachments({});
      submittedFilesRef.current = values.attachments ?? [];

      const draft: ReportDraft = {
        environment,
        eventId,
        browserSessionId,
        clientReportNonce: nonce,
        incidentType: values.incidentType,
        occurredAt: new Date().toISOString(),
        description: values.description,
        symptomHints: values.symptomHints,
        severityHint: values.severityHint,
        attachDiagnostics: values.attachDiagnostics,
        attachments: values.attachments,
        hubVersion,
        spatialManagerVersion,
      };

      try {
        const res = await runReportItSubmission(
          {
            capture: captureReportItSnapshot,
            submit: submitReport,
            getVideoElement,
            getStats,
            // Read directly from the page-lifetime store: connection-state
            // history is meaningful even for reports filed after leaving the
            // experience ("it kept freezing, so I quit").
            getConnectionState: getStreamConnectionSnapshot,
            // Prefer the page-lifetime rolling window so an intermittent freeze
            // the user just noticed is already captured (MV-1077). Falls back to
            // an on-demand window inside capture when the buffer is empty.
            getBufferedRtcSamples,
            // Scrubbed browser console tail at submit time (MV-1396).
            getBrowserLogs: getScrubbedBrowserLogs,
            getEntities: entities ? () => entities : undefined,
            onPhaseChange: setPhase,
            onAttachmentProgress: (index, fraction) =>
              setUploadProgress((prev) => ({ ...prev, [index]: fraction })),
            onAttachmentOutcome: (outcome: AttachmentDeliveryOutcome) => {
              if (outcome.delivered) {
                return;
              }

              setFailedAttachments((prev) => ({
                ...prev,
                [outcome.index]: outcome.attachmentId,
              }));
            },
            onAttachmentError: () => setAttachmentUploadFailed(true),
          },
          draft,
        );

        setResult(res);
        setStatus('success');

        // NOTE (MV-1077): analysis is intentionally NOT triggered here. To save
        // cost, correlation/root-cause analysis is operator-initiated from the
        // Observability Hub (the "Analyse" button on the report entry), the same
        // way the top-error flow works. The report is persisted as `submitted`
        // and waits for an explicit analyse action.
        return res;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to submit report';
        setError(message);
        setStatus('error');
        // eslint-disable-next-line no-console
        console.error(`Failed to submit Report It report: ${message}`, {
          eventId,
        });

        return undefined;
      }
    },
    [
      environment,
      eventId,
      browserSessionId,
      entities,
      hubVersion,
      spatialManagerVersion,
      getStats,
      nonce,
      getVideoElement,
    ],
  );

  const retryAttachment = useCallback(
    async (index: number): Promise<boolean> => {
      const attachmentId = failedAttachments[index];
      const attachment = submittedFilesRef.current[index];
      const reportId = result?.reportId;

      if (!attachmentId || !attachment || !reportId) {
        return false;
      }

      setRetryingAttachments((prev) => ({ ...prev, [index]: true }));
      setUploadProgress((prev) => ({ ...prev, [index]: 0 }));

      try {
        await retryAttachmentDelivery({
          reportId,
          attachmentId,
          file: attachment.file,
          deps: {},
          onProgress: (fraction) =>
            setUploadProgress((prev) => ({ ...prev, [index]: fraction })),
        });

        setFailedAttachments((prev) => {
          const next = { ...prev };
          delete next[index];
          setAttachmentUploadFailed(Object.keys(next).length > 0);

          return next;
        });

        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Attachment retry failed';
        // eslint-disable-next-line no-console
        console.error(`Failed to retry Report It attachment: ${message}`, {
          reportId,
          attachmentId,
        });

        return false;
      } finally {
        setRetryingAttachments((prev) => {
          const next = { ...prev };
          delete next[index];

          return next;
        });
      }
    },
    [failedAttachments, result],
  );

  const reset = useCallback((): void => {
    setStatus('idle');
    setPhase('idle');
    setUploadProgress({});
    setAttachmentUploadFailed(false);
    setFailedAttachments({});
    setRetryingAttachments({});
    setError(undefined);
    setResult(undefined);
    submittedFilesRef.current = [];
    // A reset marks the start of a new report — give it a fresh idempotency
    // nonce so it doesn't collapse onto the previously submitted report.
    setNonce(generateClientReportNonce());
  }, []);

  return {
    status,
    phase,
    uploadProgress,
    attachmentUploadFailed,
    failedAttachmentIndexes: Object.keys(failedAttachments).map(Number),
    retryingAttachmentIndexes: Object.keys(retryingAttachments).map(Number),
    error,
    result,
    submit,
    retryAttachment,
    reset,
  };
}
